import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Modal } from 'react-bootstrap';
import { doc, getDoc, deleteDoc, updateDoc, collection, getDocs, addDoc, query, where, Timestamp, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { Registration } from '../../types/registration';
import InvoiceDisplay from '../Financial/InvoiceDisplay';
import { generateInvoicePDFBlob } from '../Financial/InvoicePDFGenerator';
import {
  blobToBase64,
  getPrimaryCamperName,
  queueInvoiceEmail,
  buildCustomInvoiceEmailBody,
} from '../../utils/invoiceEmail';
import { computeRegistrationTotal } from '../../utils/invoiceModel';
import RegistrationInfoCard from './RegistrationDetails/RegistrationInfoCard';
import StayDetailsCard from './RegistrationDetails/StayDetailsCard';
import AccommodationAssignmentCard from './RegistrationDetails/AccommodationAssignmentCard';
import AttendingCampersCard from './RegistrationDetails/AttendingCampersCard';
import StayHistoryCard from './RegistrationDetails/StayHistoryCard';
import NotesCard from './RegistrationDetails/NotesCard';
import HoldReservationCard, { HoldReservationCardRef } from './RegistrationDetails/HoldReservationCard';
import ReleaseReservationCard, { ReleaseReservationCardRef } from './RegistrationDetails/ReleaseReservationCard';
import UndoReservationCard from './RegistrationDetails/UndoReservationCard';
import RefundModal from './RegistrationDetails/RefundModal';
import './InvoiceModal.css';

// Helper function to calculate number of nights between dates
const calculateNights = (arrivalDate: any, departureDate: any): number => {
  if (!arrivalDate || !departureDate) return 0;
  
  const arrival = new Date(arrivalDate);
  const departure = new Date(departureDate);
  const timeDiff = departure.getTime() - arrival.getTime();
  const nights = Math.ceil(timeDiff / (1000 * 3600 * 24));
  
  return nights > 0 ? nights : 0;
};

// Helper function to format payment dates (handles Firestore Timestamps)
const formatPaymentDate = (date: any): string => {
  if (!date) return 'N/A';
  
  try {
    // Handle Firestore Timestamp (has seconds and nanoseconds)
    if (date && typeof date.seconds === 'number') {
      return new Date(date.seconds * 1000).toLocaleDateString();
    }
    // Handle Firestore Timestamp with toDate method
    if (date && typeof date.toDate === 'function') {
      return date.toDate().toLocaleDateString();
    }
    // Handle Firestore Timestamp class
    if (date instanceof Timestamp) {
      return date.toDate().toLocaleDateString();
    }
    // Handle regular Date or parseable string/number
    return new Date(date).toLocaleDateString();
  } catch {
    return 'Invalid Date';
  }
};

const parseDateValue = (dateValue: any): Date | null => {
  if (!dateValue) return null;
  if (typeof dateValue?.toDate === 'function') {
    return dateValue.toDate();
  }
  if (dateValue instanceof Timestamp) {
    return dateValue.toDate();
  }
  if (dateValue instanceof Date) {
    return dateValue;
  }
  if (typeof dateValue === 'string') {
    const datePart = dateValue.split('T')[0];
    const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0, 0);
    }
  }
  const parsed = new Date(dateValue);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toInputDateString = (date: Date | null): string | undefined => {
  if (!date) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const shiftDateByDays = (dateValue: any, days: number): string | undefined => {
  const parsedDate = parseDateValue(dateValue);
  if (!parsedDate) return undefined;
  const shiftedDate = new Date(parsedDate);
  shiftedDate.setDate(shiftedDate.getDate() + days);
  return toInputDateString(shiftedDate);
};

const VALID_ATTENDING_AGE_CATEGORIES = new Set(['Adult', 'AdultStudent', 'YouthChildren']);

const RegistrationDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const archiveYear = searchParams.get('archive');
  const rawReturnTo = searchParams.get('returnTo');
  const defaultAssignmentsPath = archiveYear ? `/admin/assignments?view=${archiveYear}` : '/admin/assignments';
  const assignmentsReturnPath =
    rawReturnTo && rawReturnTo.startsWith('/admin/assignments')
      ? rawReturnTo
      : defaultAssignmentsPath;
  const isArchiveView = !!archiveYear;
  const [registration, setRegistration] = useState<Registration | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditDates, setShowEditDates] = useState(false);
  const [campSettings, setCampSettings] = useState<any>(null);
  const [editArrivalDate, setEditArrivalDate] = useState('');
  const [editDepartureDate, setEditDepartureDate] = useState('');
  
  // Refs for reservation cards
  const holdReservationRef = useRef<HoldReservationCardRef>(null);
  const releaseReservationRef = useRef<ReleaseReservationCardRef>(null);
  
  // Override state
  const [overrides, setOverrides] = useState({
    accommodation: { amount: 0, notes: '' },
    registration: { amount: 0, notes: '' },
    mealFees: { amount: 0, notes: '' },
    otherFees: { amount: 0, notes: '' }
  });
  
  // Override visibility state
  const [showOverrides, setShowOverrides] = useState({
    accommodation: false,
    registration: false,
    mealFees: false,
    otherFees: false
  });
  
  // Payment History state
  const [payments, setPayments] = useState<any[]>([]);
  const [newPayment, setNewPayment] = useState({
    method: '',
    amount: '',
    referenceNote: ''
  });
  const [isAddingPayment, setIsAddingPayment] = useState(false);
  const [undoingPaymentId, setUndoingPaymentId] = useState<string | null>(null);
  
  // PAR balance state
  const [parBalance, setParBalance] = useState<number | null>(null);
  const [parLoading, setParLoading] = useState(false);
  
  // Total balance from Financial Summary
  const [totalBalance, setTotalBalance] = useState<number>(0);
  
  // Invoice modal state
  const [showInvoice, setShowInvoice] = useState(false);

  // Custom "Email Invoice Again" modal state
  const [showCustomEmailModal, setShowCustomEmailModal] = useState(false);
  const [customEmailBody, setCustomEmailBody] = useState('');
  
  // Meal fees edit modal state
  const [showMealFeesModal, setShowMealFeesModal] = useState(false);
  const [editMealFees, setEditMealFees] = useState({
    fullCampAdult: 0,
    fullCampChild: 0,
    dailyAdult: 0,
    dailyChild: 0
  });
  
  // Fridge and Donation state
  const [showDonationInput, setShowDonationInput] = useState(false);
  const [donationAmount, setDonationAmount] = useState<number>(0);
  const [donationNote, setDonationNote] = useState<string>('');
  
  // Other Fees state (admin-only)
  const [showOtherFeesInput, setShowOtherFeesInput] = useState(false);
  const [otherFeesAmount, setOtherFeesAmount] = useState<number>(0);
  const [otherFeesNote, setOtherFeesNote] = useState<string>('');
  
  // Refund modal state
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedPaymentForRefund, setSelectedPaymentForRefund] = useState<any>(null);

  // Resend invoice state
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [invoicePdfDateOverride, setInvoicePdfDateOverride] = useState<string | null>(null);

  const campStartMinDate = shiftDateByDays(campSettings?.startDate, -1);
  const campEndMaxDate = shiftDateByDays(campSettings?.endDate, 1);
  
  // Calculate total balance when data changes
  useEffect(() => {
    if (!registration || !campSettings) return;

    // Calculate grandTotal via the shared helper so this page, the Assignment
    // balance column, and the Accounts Receivable report all stay in sync.
    const grandTotal = computeRegistrationTotal(registration, campSettings);

    // Calculate total balance
    const totalPayments = payments.reduce((sum, payment) => sum + payment.amount, 0);
    const calculatedTotalBalance = grandTotal - totalPayments;
    setTotalBalance(calculatedTotalBalance);
  }, [registration, campSettings, payments]);

  const invoiceRegistrationFees = useMemo(() => {
    if (!registration) return [];

    // Prefer deriving invoice rows from attending campers to match Financial Summary.
    if (registration.attendingCampers && registration.attendingCampers.length > 0 && campSettings?.rates?.registration) {
      return registration.attendingCampers.map((camper: any) => {
        let nights = camper.nights;
        if (!nights && registration?.campDates?.arrivalDate && registration?.campDates?.departureDate) {
          nights = calculateNights(registration.campDates.arrivalDate, registration.campDates.departureDate);
        }
        nights = nights || 0;

        const isFullCamp = nights >= 5;
        let fee = 0;
        let category = 'Adult';

        if (camper.age === 'Adult') {
          category = 'Adult';
          const rate = isFullCamp
            ? (campSettings.rates.registration.adult?.weekly || 0)
            : (campSettings.rates.registration.adult?.nightly || 0);
          fee = isFullCamp ? rate : rate * nights;
        } else if (camper.age === 'AdultStudent') {
          category = 'Adult Student';
          const rate = isFullCamp
            ? (campSettings.rates.registration.adultStudent?.weekly || 0)
            : (campSettings.rates.registration.adultStudent?.nightly || 0);
          fee = isFullCamp ? rate : rate * nights;
        } else if (camper.age === 'YouthChildren') {
          category = 'Youth/Children';
          const rate = isFullCamp
            ? (campSettings.rates.registration.youthAndChildren?.weekly || 0)
            : (campSettings.rates.registration.youthAndChildren?.nightly || 0);
          fee = isFullCamp ? rate : rate * nights;
        }

        return {
          name: `${camper.firstName || ''} ${camper.lastName || ''}`.trim() || 'Camper',
          category,
          nights,
          fee
        };
      });
    }

    // Fallback for legacy records missing attending camper fee context.
    return [
      ...((registration as any).financial?.registrationFees?.adults || []).map((adult: any) => ({
        name: adult.name,
        category: 'Adult',
        nights: adult.nights || 0,
        fee: adult.fee
      })),
      ...((registration as any).financial?.registrationFees?.adultStudents || []).map((student: any) => ({
        name: student.name,
        category: 'Adult Student',
        nights: student.nights || 0,
        fee: student.fee
      })),
      ...((registration as any).financial?.registrationFees?.youthChildren || []).map((youth: any) => ({
        name: youth.name,
        category: 'Youth/Children',
        nights: youth.nights || 0,
        fee: youth.fee
      }))
    ];
  }, [registration, campSettings]);

  const invoicePaymentTotal = useMemo(
    () => ((registration as any)?.paymentHistory || []).reduce(
      (sum: number, payment: any) => sum + (payment.amount || 0),
      0
    ),
    [registration]
  );

  const invoiceDisplayRegistration = useMemo(() => {
    if (!registration) return null;

    return {
      id: registration.id,
      primaryCamper: {
        firstName: (registration as any).registrationInfo?.primaryCamperName?.split(' ')[0] || registration.firstName || '',
        lastName: (registration as any).registrationInfo?.primaryCamperName?.split(' ').slice(1).join(' ') || registration.lastName || '',
        email: (registration as any).registrationInfo?.email || registration.email || '',
        address: (registration as any).registrationInfo?.address?.street || '',
        city: (registration as any).registrationInfo?.address?.town || '',
        province: (registration as any).registrationInfo?.address?.province || '',
        postalCode: (registration as any).registrationInfo?.address?.postalCode || '',
      },
      accommodationFees: {
        placeName: registration.financial?.accommodationFees?.placeName || '',
        placeCode: registration.financial?.accommodationFees?.priceCode || '',
        fee: registration.financial?.accommodationFees?.accommodationFee || 0,
        numberOfNights: (registration.financial?.accommodationFees as any)?.numberOfNights || 0,
        override: (registration as any).financial?.overrides?.accommodation?.enabled ? {
          amount: (registration as any).financial.overrides.accommodation.amount,
          note: (registration as any).financial.overrides.accommodation.notes
        } : undefined
      },
      registrationFees: invoiceRegistrationFees,
      registrationOverride: (registration as any).financial?.overrides?.registration?.amount !== undefined && (registration as any).financial?.overrides?.registration?.amount !== 0 ? {
        amount: (registration as any).financial.overrides.registration.amount,
        note: (registration as any).financial.overrides.registration.notes
      } : undefined,
      mealFees: {
        fullCampAdult: (registration as any).financial?.otherFees?.meals?.fullCampPassesAdult ? {
          quantity: (registration as any).financial.otherFees.meals.fullCampPassesAdult.quantity || 0,
          unitPrice: (registration as any).financial.otherFees.meals.fullCampPassesAdult.unitPrice || 0,
          total: (registration as any).financial.otherFees.meals.fullCampPassesAdult.total || 0
        } : undefined,
        fullCampChild: (registration as any).financial?.otherFees?.meals?.fullCampPassesChild ? {
          quantity: (registration as any).financial.otherFees.meals.fullCampPassesChild.quantity || 0,
          unitPrice: (registration as any).financial.otherFees.meals.fullCampPassesChild.unitPrice || 0,
          total: (registration as any).financial.otherFees.meals.fullCampPassesChild.total || 0
        } : undefined,
        dailyAdult: (registration as any).financial?.otherFees?.meals?.dailyPassesAdult ? {
          quantity: (registration as any).financial.otherFees.meals.dailyPassesAdult.quantity || 0,
          unitPrice: (registration as any).financial.otherFees.meals.dailyPassesAdult.unitPrice || 0,
          total: (registration as any).financial.otherFees.meals.dailyPassesAdult.total || 0
        } : undefined,
        dailyChild: (registration as any).financial?.otherFees?.meals?.dailyPassesChild ? {
          quantity: (registration as any).financial.otherFees.meals.dailyPassesChild.quantity || 0,
          unitPrice: (registration as any).financial.otherFees.meals.dailyPassesChild.unitPrice || 0,
          total: (registration as any).financial.otherFees.meals.dailyPassesChild.total || 0
        } : undefined,
        override: (registration as any).financial?.overrides?.mealFees?.amount !== undefined && (registration as any).financial?.overrides?.mealFees?.amount !== 0 ? {
          amount: (registration as any).financial.overrides.mealFees.amount,
          note: (registration as any).financial.overrides.mealFees.notes
        } : undefined
      },
      otherFees: {
        fridgeRental: (registration as any).financial?.otherFees?.fridge?.cost || 0,
        donation: (registration as any).financial?.otherFees?.donations?.amount || 0,
        donationNote: (registration as any).financial?.otherFees?.donations?.note || '',
        customFee: (registration as any).financial?.otherFees?.customFee?.amount || 0,
        customFeeNote: (registration as any).financial?.otherFees?.customFee?.note || '',
        otherFees: (registration as any).financial?.otherFees?.otherFees || [],
        override: (registration as any).financial?.overrides?.otherFees?.amount !== undefined && (registration as any).financial?.overrides?.otherFees?.amount !== 0 ? {
          amount: (registration as any).financial.overrides.otherFees.amount,
          note: (registration as any).financial.overrides.otherFees.notes
        } : undefined
      },
      totalAmount: totalBalance,
      paymentAmount: invoicePaymentTotal,
      remainingBalance: totalBalance - invoicePaymentTotal,
    };
  }, [registration, invoiceRegistrationFees, totalBalance, invoicePaymentTotal]);

  const invoiceDateForDisplay = invoicePdfDateOverride ?? (registration as any)?.financial?.invoiceDate ?? null;
  

  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        console.log('No ID provided');
        navigate(assignmentsReturnPath);
        return;
      }
      
      console.log('Fetching registration with ID:', id);
      
      try {
        // Fetch registration — from archive or live collection
        const registrationRef = archiveYear
          ? doc(db, 'archive', archiveYear, 'registrations', id)
          : doc(db, 'Registrations', id);
        const registrationSnap = await getDoc(registrationRef);
        
        if (registrationSnap.exists()) {
          console.log('Registration found:', registrationSnap.data());
          const regData = { id: registrationSnap.id, ...registrationSnap.data() } as Registration;
          setRegistration(regData);
          
          // Load existing overrides
          if (regData.financial?.overrides) {
            setOverrides({
              accommodation: regData.financial.overrides.accommodation || { amount: 0, notes: '' },
              registration: regData.financial.overrides.registration || { amount: 0, notes: '' },
              mealFees: regData.financial.overrides.mealFees || { amount: 0, notes: '' },
              otherFees: regData.financial.overrides.otherFees || { amount: 0, notes: '' }
            });
            
            // Load override visibility state
            setShowOverrides({
              accommodation: (regData.financial.overrides.accommodation as any)?.enabled || false,
              registration: (regData.financial.overrides.registration?.amount !== undefined && regData.financial.overrides.registration?.amount !== 0) || false,
              mealFees: (regData.financial.overrides.mealFees?.amount !== undefined && regData.financial.overrides.mealFees?.amount !== 0) || false,
              otherFees: (regData.financial.overrides.otherFees?.amount !== undefined && regData.financial.overrides.otherFees?.amount !== 0) || false
            });
          }
          
          // Load payment history
          if ((regData as any).paymentHistory && Array.isArray((regData as any).paymentHistory)) {
            setPayments((regData as any).paymentHistory);
          }
          
          // Fetch PAR balance for primary camper
          if (regData.registrationInfo?.email) {
            setParLoading(true);
            try {
              const primaryCampersQuery = query(
                collection(db, 'primaryCampers'),
                where('email', '==', regData.registrationInfo.email)
              );
              const primaryCampersSnapshot = await getDocs(primaryCampersQuery);
              
              if (!primaryCampersSnapshot.empty) {
                const primaryCamperData = primaryCampersSnapshot.docs[0].data();
                setParBalance(primaryCamperData.par || 0);
              } else {
                setParBalance(0);
              }
            } catch (error) {
              console.error('Error fetching PAR balance:', error);
              setParBalance(0);
            } finally {
              setParLoading(false);
            }
          }
        } else {
          console.error('Registration not found for ID:', id);
          navigate(assignmentsReturnPath);
        }

        // Fetch camp settings
        const settingsRef = doc(db, 'settings', 'camp');
        const settingsSnap = await getDoc(settingsRef);
        
        if (settingsSnap.exists()) {
          const settingsData = settingsSnap.data();
          console.log('Camp settings found:', settingsData);
          console.log('Camp startDate type:', typeof settingsData?.startDate, 'value:', settingsData?.startDate);
          console.log('Camp endDate type:', typeof settingsData?.endDate, 'value:', settingsData?.endDate);
          setCampSettings(settingsData);
        } else {
          console.error('Camp settings not found');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        navigate(assignmentsReturnPath);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate, archiveYear, assignmentsReturnPath]);

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!registration) return;

    setIsDeleting(true);
    try {
      await deleteDoc(doc(db, 'Registrations', registration.id));
      navigate(assignmentsReturnPath);
      // You could add a success message here if needed
    } catch (error) {
      console.error('Error deleting registration:', error);
      alert('Failed to delete registration. Please try again.');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const handleEditDatesClick = () => {
    console.log('Opening edit dates modal');
    console.log('Current campDates:', registration?.campDates);
    const defaultArrivalDate = campStartMinDate || '';
    const defaultDepartureDate = campEndMaxDate || '';
    
    if (registration?.campDates?.arrivalDate && registration?.campDates?.departureDate) {
      // Convert dates to YYYY-MM-DD format for input fields
      const arrivalDate = toInputDateString(parseDateValue(registration.campDates.arrivalDate)) || defaultArrivalDate;
      const departureDate = toInputDateString(parseDateValue(registration.campDates.departureDate)) || defaultDepartureDate;
      console.log('Setting dates:', arrivalDate, departureDate);
      setEditArrivalDate(arrivalDate);
      setEditDepartureDate(departureDate);
    } else {
      // Set default dates if none exist
      console.log('No existing dates, setting defaults');
      setEditArrivalDate(defaultArrivalDate);
      setEditDepartureDate(defaultDepartureDate);
    }
    setShowEditDates(true);
  };

  const handleEditDatesCancel = () => {
    setShowEditDates(false);
    setEditArrivalDate('');
    setEditDepartureDate('');
  };

  const handleEditDatesSave = async () => {
    if (!registration || !editArrivalDate || !editDepartureDate) return;

    // Validate dates
    const arrival = parseDateValue(editArrivalDate);
    const departure = parseDateValue(editDepartureDate);
    if (!arrival || !departure) {
      alert('Please select valid arrival and departure dates.');
      return;
    }
    
    if (departure <= arrival) {
      alert('Departure date must be after arrival date');
      return;
    }

    if (campSettings?.startDate && campSettings?.endDate) {
      const campStart = parseDateValue(campSettings.startDate);
      const campEnd = parseDateValue(campSettings.endDate);
      if (!campStart || !campEnd) {
        alert('Camp date settings are invalid. Please contact admin.');
        return;
      }
      campStart.setDate(campStart.getDate() - 1);
      campEnd.setDate(campEnd.getDate() + 1);
      
      if (arrival < campStart || departure > campEnd) {
        alert(`Dates must be within allowed range: ${campStart.toLocaleDateString()} to ${campEnd.toLocaleDateString()}`);
        return;
      }
    }

    try {
      // Calculate new nights
      const timeDiff = departure.getTime() - arrival.getTime();
      const nights = Math.ceil(timeDiff / (1000 * 3600 * 24));
      const isFullCamp = nights >= 5;
      
      // Recalculate accommodation fee if accommodation is assigned
      let accommodationFee = 0;
      if (registration.financial?.accommodationFees?.priceCode && campSettings?.rates?.accommodation) {
        const priceCode = String(registration.financial.accommodationFees.priceCode).toUpperCase();
        const dormRates = campSettings.rates.accommodation.dorm;

        // Dorm/Fireside fix: D1/D2 always come from the dorm rates bucket.
        if (priceCode.startsWith('D') && dormRates?.[priceCode]) {
          const rate = dormRates[priceCode];
          if (rate.weekly && rate.nightly) {
            accommodationFee = isFullCamp ? rate.weekly : (rate.nightly * nights);
          }
        } else {
          const accommodationTypes = ['apartments', 'campsite', 'cottages', 'fireside', 'lowerCourt', 'upperCourt', 'dormA', 'dormB', 'privateCottage'];
          
          for (const type of accommodationTypes) {
            if (campSettings.rates.accommodation[type] && campSettings.rates.accommodation[type][priceCode]) {
              const rate = campSettings.rates.accommodation[type][priceCode];
              if (rate.weekly && rate.nightly) {
                accommodationFee = isFullCamp ? rate.weekly : (rate.nightly * nights);
                break;
              }
            }
          }
        }
      }
      
      const registrationRef = doc(db, 'Registrations', registration.id);
      const updateData: any = {
        'campDates.arrivalDate': editArrivalDate,
        'campDates.departureDate': editDepartureDate
      };
      
      // Update accommodation fee and nights if accommodation is assigned
      if (registration.financial?.accommodationFees?.assignedAccommodation) {
        updateData['financial.accommodationFees.accommodationFee'] = accommodationFee;
        updateData['financial.accommodationFees.numberOfNights'] = nights;
      }
      
      await updateDoc(registrationRef, updateData);
      
      // Update local state
      setRegistration(prev => {
        if (!prev) return null;
        
        const updated: any = {
          ...prev,
          campDates: {
            ...prev.campDates,
            arrivalDate: editArrivalDate,
            departureDate: editDepartureDate
          }
        };
        
        // Update accommodation fees if accommodation is assigned
        if (prev.financial?.accommodationFees?.assignedAccommodation) {
          updated.financial = {
            ...prev.financial,
            accommodationFees: {
              ...prev.financial.accommodationFees,
              accommodationFee: accommodationFee,
              numberOfNights: nights
            }
          };
        }
        
        return updated;
      });
      
      setShowEditDates(false);
      setEditArrivalDate('');
      setEditDepartureDate('');
    } catch (error) {
      console.error('Error updating dates:', error);
      alert('Failed to update dates. Please try again.');
    }
  };


  // Override functions
  const handleOverrideChange = async (section: string, field: 'amount' | 'notes', value: string | number) => {
    if (!registration) return;
    
    const sectionKey = section as keyof typeof overrides;
    const newSectionOverride = {
      ...overrides[sectionKey],
      [field]: value
    };
    const newOverrides = {
      ...overrides,
      [section]: newSectionOverride
    };
    
    setOverrides(newOverrides);
    
    // Save only the changed field so we don't overwrite persisted flags like "enabled"
    try {
      const registrationRef = doc(db, 'Registrations', registration.id);
      await updateDoc(registrationRef, {
        [`financial.overrides.${section}.${field}`]: value
      });
      
      // Update local state
      setRegistration(prev => prev ? {
        ...prev,
        financial: {
          ...prev.financial,
          overrides: {
            ...prev.financial?.overrides,
            [section]: {
              ...prev.financial?.overrides?.[section as keyof typeof prev.financial.overrides],
              ...newSectionOverride
            }
          }
        }
      } : null);
    } catch (error) {
      console.error('Error updating overrides:', error);
      alert('Failed to update override. Please try again.');
    }
  };

  const handleOverrideToggle = async (section: string) => {
    if (!registration) return;
    
    const newShowOverrides = {
      ...showOverrides,
      [section]: !showOverrides[section as keyof typeof showOverrides]
    };
    
    setShowOverrides(newShowOverrides);
    
    // If unchecking, clear the override values
    if (!newShowOverrides[section as keyof typeof newShowOverrides]) {
      const clearedOverrides = {
        ...overrides,
        [section]: { amount: 0, notes: '' }
      };
      setOverrides(clearedOverrides);
    }
    
    // Save to Firestore
    try {
      const registrationRef = doc(db, 'Registrations', registration.id);
      const updateData: any = {
        [`financial.overrides.${section}.enabled`]: newShowOverrides[section as keyof typeof newShowOverrides]
      };
      
      // If unchecking, also clear the amount and notes
      if (!newShowOverrides[section as keyof typeof newShowOverrides]) {
        updateData[`financial.overrides.${section}.amount`] = 0;
        updateData[`financial.overrides.${section}.notes`] = '';
      }
      
      await updateDoc(registrationRef, updateData);
      
      // Update local state
      setRegistration(prev => prev ? {
        ...prev,
        financial: {
          ...prev.financial,
          overrides: {
            ...prev.financial?.overrides,
            [section]: {
              ...prev.financial?.overrides?.[section as keyof typeof prev.financial.overrides],
              enabled: newShowOverrides[section as keyof typeof newShowOverrides],
              ...(newShowOverrides[section as keyof typeof newShowOverrides] ? {} : { amount: 0, notes: '' })
            }
          }
        }
      } : null);
    } catch (error) {
      console.error('Error updating override visibility:', error);
      alert('Failed to update override state. Please try again.');
    }
  };

  // Payment History functions
  const handleAddPayment = async () => {
    if (!registration || !newPayment.method || !newPayment.amount) return;
    
    const payment = {
      id: Date.now().toString(), // Simple ID generation
      date: new Date().toISOString(),
      method: newPayment.method,
      amount: parseFloat(newPayment.amount),
      referenceNote: newPayment.referenceNote
    };
    
    const updatedPayments = [...payments, payment];
    setPayments(updatedPayments);
    
    // Save to Firestore
    try {
      const registrationRef = doc(db, 'Registrations', registration.id);
      await updateDoc(registrationRef, {
        paymentHistory: updatedPayments
      });
      
      // Update local state
      setRegistration(prev => prev ? {
        ...prev,
        paymentHistory: updatedPayments
      } as any : null);
      
      // Reset form
      setNewPayment({ method: '', amount: '', referenceNote: '' });
      setIsAddingPayment(false);
    } catch (error) {
      console.error('Error adding payment:', error);
      alert('Failed to add payment. Please try again.');
    }
  };

  const handleDeletePayment = async (paymentId: string) => {
    if (!registration) return;
    
    if (!window.confirm('Are you sure you want to delete this payment?')) {
      return;
    }

    const paymentToDelete = payments.find(p => p.id === paymentId);
    if (!paymentToDelete) return;

    const updatedPayments = payments.filter(p => p.id !== paymentId);
    
    // Save to Firestore
    try {
      const registrationRef = doc(db, 'Registrations', registration.id);
      await updateDoc(registrationRef, {
        paymentHistory: updatedPayments
      });

      // If deleting a PAR payment, return its amount back to the primary camper PAR balance.
      const isParPayment = String(paymentToDelete.method || '').trim().toLowerCase() === 'par';
      const deletedAmount = Number(paymentToDelete.amount) || 0;
      if (isParPayment && deletedAmount > 0) {
        const newParBalance = (parBalance || 0) + deletedAmount;

        if (registration.registrationInfo?.email) {
          const primaryCampersQuery = query(
            collection(db, 'primaryCampers'),
            where('email', '==', registration.registrationInfo.email)
          );
          const primaryCampersSnapshot = await getDocs(primaryCampersQuery);

          if (!primaryCampersSnapshot.empty) {
            const primaryCamperRef = doc(db, 'primaryCampers', primaryCampersSnapshot.docs[0].id);
            await updateDoc(primaryCamperRef, {
              par: newParBalance
            });
            setParBalance(newParBalance);
          }
        }
      }
      
      // Update local state
      setPayments(updatedPayments);
      setRegistration(prev => prev ? {
        ...prev,
        paymentHistory: updatedPayments
      } as any : null);
    } catch (error) {
      console.error('Error deleting payment:', error);
      alert('Failed to delete payment. Please try again.');
    }
  };

  // Undo an e-transfer that was applied from the inbox: removes this payment and
  // returns the transfer to the e-transfer inbox as un-applied (never deletes the transfer).
  const handleUndoEtransfer = async (payment: any) => {
    if (!registration || !payment?.etransferInboxId) return;

    if (!window.confirm(
      'Undo this applied e-transfer? The payment will be removed here and the e-transfer will be returned to the inbox as un-applied. The e-transfer itself will not be deleted.'
    )) {
      return;
    }

    setUndoingPaymentId(payment.id);
    try {
      const undoPayment = httpsCallable(functions, 'undoEtransferInboxPayment');
      await undoPayment({ inboxItemId: payment.etransferInboxId });

      // Re-fetch the registration so the payment list reflects the server-side change.
      const registrationRef = doc(db, 'Registrations', registration.id);
      const snap = await getDoc(registrationRef);
      if (snap.exists()) {
        const data = { id: snap.id, ...(snap.data() as any) };
        setRegistration(data as any);
        setPayments(Array.isArray(data.paymentHistory) ? data.paymentHistory : []);
      }
    } catch (error: any) {
      console.error('Error undoing e-transfer:', error);
      alert(error?.message || 'Failed to undo e-transfer. Please try again.');
    } finally {
      setUndoingPaymentId(null);
    }
  };

  const sendInvoiceEmail = useCallback(async (
    invoiceDateOverride?: string,
    emailOptions?: { subject?: string; customBody?: string }
  ) => {
    if (!registration) {
      throw new Error('Registration not loaded');
    }

    const invoiceNumber = registration.invoiceNumber;
    if (!invoiceNumber) {
      throw new Error('Invoice number is missing for this registration.');
    }

    const recipientEmail = (registration as any).registrationInfo?.email || registration.email;
    if (!recipientEmail) {
      throw new Error('No email address found for this registration.');
    }

    const primaryCamperName = getPrimaryCamperName(registration);

    if (invoiceDateOverride) {
      setInvoicePdfDateOverride(invoiceDateOverride);
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });
    }

    const invoiceElement = document.getElementById('registration-invoice-source');
    if (!invoiceElement) {
      throw new Error('Unable to find the invoice preview to generate the PDF.');
    }

    try {
      const pdfBlob = await generateInvoicePDFBlob(invoiceElement.outerHTML);
      const pdfBase64 = await blobToBase64(pdfBlob);

      await queueInvoiceEmail({
        recipientEmail,
        primaryCamperName,
        invoiceNumber,
        pdfBase64,
        subject: emailOptions?.subject,
        body: emailOptions?.customBody
          ? buildCustomInvoiceEmailBody(primaryCamperName, emailOptions.customBody)
          : undefined,
      });
    } finally {
      if (invoiceDateOverride) {
        setInvoicePdfDateOverride(null);
      }
    }
  }, [registration]);

  const handleResendInvoice = async () => {
    if (!registration) return;

    const invalidAttendingCampers = (registration.attendingCampers || []).filter((camper: any) => {
      const camperAge = camper?.age ?? camper?.category;
      return !VALID_ATTENDING_AGE_CATEGORIES.has(String(camperAge || ''));
    });
    if (invalidAttendingCampers.length > 0) {
      const invalidCamperList = invalidAttendingCampers
        .map((camper: any) => `${camper?.firstName || ''} ${camper?.lastName || ''}`.trim() || 'Unnamed camper')
        .join(', ');
      alert(`Invoice blocked. Please fix attending camper age category for: ${invalidCamperList}.`);
      return;
    }

    const recipientEmail = (registration as any).registrationInfo?.email || registration.email;
    if (!recipientEmail) {
      alert('No email address found for this registration.');
      return;
    }

    if (!window.confirm(`Send invoice to ${recipientEmail}?`)) return;

    setSendingInvoice(true);
    try {
      await sendInvoiceEmail();
      alert('Invoice email sent successfully.');
    } catch (error) {
      console.error('Error sending invoice:', error);
      alert('Failed to send invoice email. Please try again.');
    } finally {
      setSendingInvoice(false);
    }
  };

  const handleOpenCustomEmailModal = () => {
    if (!registration) return;

    const invalidAttendingCampers = (registration.attendingCampers || []).filter((camper: any) => {
      const camperAge = camper?.age ?? camper?.category;
      return !VALID_ATTENDING_AGE_CATEGORIES.has(String(camperAge || ''));
    });
    if (invalidAttendingCampers.length > 0) {
      const invalidCamperList = invalidAttendingCampers
        .map((camper: any) => `${camper?.firstName || ''} ${camper?.lastName || ''}`.trim() || 'Unnamed camper')
        .join(', ');
      alert(`Invoice blocked. Please fix attending camper age category for: ${invalidCamperList}.`);
      return;
    }

    const recipientEmail = (registration as any).registrationInfo?.email || registration.email;
    if (!recipientEmail) {
      alert('No email address found for this registration.');
      return;
    }

    setCustomEmailBody('');
    setShowCustomEmailModal(true);
  };

  const handleSendCustomInvoiceEmail = async () => {
    if (!registration) return;

    if (!customEmailBody.trim()) {
      alert('Please enter a message for the email body.');
      return;
    }

    setSendingInvoice(true);
    try {
      await sendInvoiceEmail(undefined, {
        subject: 'Berwick Camp Information',
        customBody: customEmailBody,
      });
      setShowCustomEmailModal(false);
      setCustomEmailBody('');
      alert('Invoice email sent successfully.');
    } catch (error) {
      console.error('Error sending invoice:', error);
      alert('Failed to send invoice email. Please try again.');
    } finally {
      setSendingInvoice(false);
    }
  };

  const handleSaveMealFees = async () => {
    if (!registration || !campSettings) return;
    
    try {
      const rates = campSettings.rates?.meals || {};
      const fullCampAdultRate = rates.fullCampAdult || 100;
      const fullCampChildRate = rates.fullCampChild || 50;
      const dailyAdultRate = rates.dailyAdult || 15;
      const dailyChildRate = rates.dailyChild || 10;
      
      const newMeals = {
        fullCampPassesAdult: {
          quantity: editMealFees.fullCampAdult,
          unitPrice: fullCampAdultRate,
          total: editMealFees.fullCampAdult * fullCampAdultRate
        },
        fullCampPassesChild: {
          quantity: editMealFees.fullCampChild,
          unitPrice: fullCampChildRate,
          total: editMealFees.fullCampChild * fullCampChildRate
        },
        dailyPassesAdult: {
          quantity: editMealFees.dailyAdult,
          unitPrice: dailyAdultRate,
          total: editMealFees.dailyAdult * dailyAdultRate
        },
        dailyPassesChild: {
          quantity: editMealFees.dailyChild,
          unitPrice: dailyChildRate,
          total: editMealFees.dailyChild * dailyChildRate
        },
        totalMealCost: 
          (editMealFees.fullCampAdult * fullCampAdultRate) +
          (editMealFees.fullCampChild * fullCampChildRate) +
          (editMealFees.dailyAdult * dailyAdultRate) +
          (editMealFees.dailyChild * dailyChildRate)
      };
      
      const registrationRef = doc(db, 'Registrations', registration.id);
      await updateDoc(registrationRef, {
        'financial.otherFees.meals': newMeals
      });
      
      // Update local state
      setRegistration(prev => {
        if (!prev) return null;
        return {
          ...prev,
          financial: {
            ...prev.financial,
            otherFees: {
              ...prev.financial?.otherFees,
              meals: newMeals
            }
          }
        } as any;
      });
      
      setShowMealFeesModal(false);
    } catch (error) {
      console.error('Error saving meal fees:', error);
      alert('Failed to save meal fees. Please try again.');
    }
  };

  const handleApplyParPayment = async () => {
    if (!registration) return;
    
    if (!parBalance || parBalance <= 0) {
      alert('There are no funds in PAR.');
      return;
    }

    // Use the same balance source shown in Financial Summary to avoid schema mismatch.
    const balanceOwing = Math.max(0, totalBalance);
    const parPaymentAmount = Math.min(parBalance, balanceOwing);
    
    if (parPaymentAmount <= 0) {
      alert('No balance owing or no PAR balance available.');
      return;
    }
    
    if (!window.confirm(`Apply PAR payment of $${parPaymentAmount.toFixed(2)}?`)) {
      return;
    }
    
    try {
      // Create PAR payment
      const parPayment = {
        id: Date.now().toString(),
        date: new Date().toISOString(),
        method: 'Par',
        amount: parPaymentAmount,
        referenceNote: 'Par Payment'
      };
      
      // Update payments
      const updatedPayments = [...payments, parPayment];
      setPayments(updatedPayments);
      
      // Update PAR balance
      const newParBalance = parBalance - parPaymentAmount;
      setParBalance(newParBalance);
      
      // Save to Firestore - update registration payment history
      const registrationRef = doc(db, 'Registrations', registration.id);
      await updateDoc(registrationRef, {
        paymentHistory: updatedPayments
      });
      
      // Save to Firestore - update primary camper PAR balance
      if (registration.registrationInfo?.email) {
        const primaryCampersQuery = query(
          collection(db, 'primaryCampers'),
          where('email', '==', registration.registrationInfo.email)
        );
        const primaryCampersSnapshot = await getDocs(primaryCampersQuery);
        
        if (!primaryCampersSnapshot.empty) {
          const primaryCamperRef = doc(db, 'primaryCampers', primaryCampersSnapshot.docs[0].id);
          await updateDoc(primaryCamperRef, {
            par: newParBalance
          });
        }
      }
      
      // Update local registration state
      setRegistration(prev => prev ? {
        ...prev,
        paymentHistory: updatedPayments
      } as any : null);
      
      alert(`Successfully applied PAR payment of $${parPaymentAmount.toFixed(2)}`);
    } catch (error) {
      console.error('Error applying PAR payment:', error);
      alert('Failed to apply PAR payment. Please try again.');
    }
  };

  // Handle fridge rental toggle
  const handleFridgeToggle = async (checked: boolean) => {
    if (!registration) return;
    
    try {
      const registrationRef = doc(db, 'Registrations', registration.id);
      const fridgeRate = campSettings?.rates?.otherFees?.fridge || 25;
      
      if (checked) {
        // Add fridge rental
        await updateDoc(registrationRef, {
          'financial.otherFees.fridge': {
            requested: true,
            cost: fridgeRate
          }
        });
        
        setRegistration(prev => prev ? {
          ...prev,
          financial: {
            ...prev.financial,
            otherFees: {
              ...prev.financial?.otherFees,
              fridge: { requested: true, cost: fridgeRate }
            }
          }
        } : null);
      } else {
        // Remove fridge rental
        await updateDoc(registrationRef, {
          'financial.otherFees.fridge': null
        });
        
        setRegistration(prev => prev ? {
          ...prev,
          financial: {
            ...prev.financial,
            otherFees: {
              ...prev.financial?.otherFees,
              fridge: undefined
            }
          }
        } : null);
      }
    } catch (error) {
      console.error('Error updating fridge rental:', error);
      alert('Failed to update fridge rental. Please try again.');
    }
  };

  // Handle donation toggle
  const handleDonationToggle = async (checked: boolean) => {
    if (!registration) return;
    
    if (checked) {
      // Populate existing values if editing
      const existingDonation = (registration.financial?.otherFees as any)?.donations;
      setDonationAmount(existingDonation?.amount || 0);
      setDonationNote(existingDonation?.note || '');
      setShowDonationInput(true);
    } else {
      // Remove donation
      try {
        const registrationRef = doc(db, 'Registrations', registration.id);
        await updateDoc(registrationRef, {
          'financial.otherFees.donations': null
        });
        
        setRegistration(prev => prev ? {
          ...prev,
          financial: {
            ...prev.financial,
            otherFees: {
              ...prev.financial?.otherFees,
              donations: undefined
            }
          }
        } : null);
        
        setShowDonationInput(false);
        setDonationAmount(0);
        setDonationNote('');
      } catch (error) {
        console.error('Error removing donation:', error);
        alert('Failed to remove donation. Please try again.');
      }
    }
  };

  // Handle donation amount save
  const handleDonationSave = async () => {
    if (!registration || donationAmount <= 0) return;
    
    try {
      const registrationRef = doc(db, 'Registrations', registration.id);
      await updateDoc(registrationRef, {
        'financial.otherFees.donations': {
          amount: donationAmount,
          note: donationNote.slice(0, 256) // Limit to 256 characters
        }
      });
      
      setRegistration(prev => prev ? {
        ...prev,
        financial: {
          ...prev.financial,
          otherFees: {
            ...prev.financial?.otherFees,
            donations: { amount: donationAmount, note: donationNote.slice(0, 256) }
          }
        }
      } : null);
      
      setShowDonationInput(false);
    } catch (error) {
      console.error('Error saving donation:', error);
      alert('Failed to save donation. Please try again.');
    }
  };

  // Handle other fees toggle (admin-only)
  const handleOtherFeesToggle = async (checked: boolean) => {
    if (!registration) return;
    
    if (checked) {
      // Populate existing values if editing
      const existingOtherFees = (registration.financial?.otherFees as any)?.customFee;
      setOtherFeesAmount(existingOtherFees?.amount || 0);
      setOtherFeesNote(existingOtherFees?.note || '');
      setShowOtherFeesInput(true);
    } else {
      // Remove other fees
      try {
        const registrationRef = doc(db, 'Registrations', registration.id);
        await updateDoc(registrationRef, {
          'financial.otherFees.customFee': null
        });
        
        setRegistration(prev => prev ? {
          ...prev,
          financial: {
            ...prev.financial,
            otherFees: {
              ...prev.financial?.otherFees,
              customFee: undefined
            }
          }
        } : null);
        
        setShowOtherFeesInput(false);
        setOtherFeesAmount(0);
        setOtherFeesNote('');
      } catch (error) {
        console.error('Error removing other fees:', error);
        alert('Failed to remove other fees. Please try again.');
      }
    }
  };

  // Handle other fees save (admin-only)
  const handleOtherFeesSave = async () => {
    if (!registration || otherFeesAmount <= 0) return;
    
    try {
      const registrationRef = doc(db, 'Registrations', registration.id);
      await updateDoc(registrationRef, {
        'financial.otherFees.customFee': {
          amount: otherFeesAmount,
          note: otherFeesNote.slice(0, 256)
        }
      });
      
      setRegistration(prev => prev ? {
        ...prev,
        financial: {
          ...prev.financial,
          otherFees: {
            ...prev.financial?.otherFees,
            customFee: { amount: otherFeesAmount, note: otherFeesNote.slice(0, 256) }
          }
        }
      } : null);
      
      setShowOtherFeesInput(false);
    } catch (error) {
      console.error('Error saving other fees:', error);
      alert('Failed to save other fees. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <p>Loading registration details...</p>
        </div>
      </div>
    );
  }

  if (!registration) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <p>Registration not found.</p>
          <Button variant="secondary" onClick={() => navigate(assignmentsReturnPath)}>
            Back to Assignments
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-1">
      <div className="row">
        <div className="col-12">
          <div className="mb-4">
            <div className="ps-0">
              <h5>{isArchiveView ? `Archive: ${archiveYear} Registration Details` : 'Registration Details'}</h5>
              {isArchiveView && (
                <div style={{
                  backgroundColor: '#fff3cd',
                  border: '2px solid #ffc107',
                  borderRadius: '8px',
                  padding: '0.75rem 1.25rem',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <strong style={{ color: '#856404' }}>Read-Only Archive View</strong>
                  <span style={{ color: '#856404' }}>— Historical data from {archiveYear}. No changes can be made.</span>
                </div>
              )}
              <div className="d-flex justify-content-between mt-1">
                {!isArchiveView && (
                  <div>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={handleDeleteClick}
                      disabled={isDeleting}
                    >
                      {isDeleting ? 'Deleting...' : 'Delete Registration'}
                    </Button>
                    <div style={{ fontSize: '0.8rem', color: '#6c757d', marginTop: '0.35rem' }}>
                      Document ID: {id || '-'}
                    </div>
                  </div>
                )}
                {isArchiveView && (
                  <Button 
                    variant="secondary" 
                    size="sm"
                    onClick={() => navigate(assignmentsReturnPath)}
                  >
                    Back to Assignments
                  </Button>
                )}
                <Button 
                  variant="primary" 
                  size="sm"
                  onClick={() => setShowInvoice(true)}
                >
                  View Invoice
                </Button>
              </div>
            </div>
          </div>
          
          {/* Two-column layout for cards */}
          <div className="row" style={isArchiveView ? { pointerEvents: 'none' } : {}}>
            <div className="col-md-6">
              {/* First card - Camper Status */}
              <div className="card mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="card-body p-2">
                  <div className="form-check" style={{ marginBottom: '-5px' }}>
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="newCamper"
                      checked={registration?.NewCamper || false}
                      disabled={isArchiveView}
                      onChange={async (e) => {
                        if (!registration) return;
                        
                        const newValue = e.target.checked;
                        console.log('Updating NewCamper to:', newValue);
                        
                        try {
                          const registrationRef = doc(db, 'Registrations', registration.id);
                          await updateDoc(registrationRef, {
                            NewCamper: newValue
                          });
                          
                          // Update local state
                          setRegistration(prev => {
                            if (!prev) return null;
                            const updated = { ...prev, NewCamper: newValue };
                            console.log('Updated registration state:', updated);
                            return updated;
                          });
                        } catch (error) {
                          console.error('Error updating NewCamper status:', error);
                          alert('Failed to update New Camper status. Please try again.');
                        }
                      }}
                    />
                    <label className="form-check-label" htmlFor="newCamper">
                      <span style={{ fontWeight: 'bold', color: '#333' }}>New Camper</span>
                    </label>
                  </div>
                  
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="arrived"
                      checked={registration?.Arrived ? true : false}
                      disabled={isArchiveView}
                      onChange={async (e) => {
                        if (!registration) return;
                        
                        const newValue = e.target.checked ? new Date() : null;
                        console.log('Updating Arrived to:', newValue);
                        
                        try {
                          const registrationRef = doc(db, 'Registrations', registration.id);
                          await updateDoc(registrationRef, {
                            Arrived: newValue
                          });
                          
                          // Update local state
                          setRegistration(prev => {
                            if (!prev) return null;
                            const updated = { ...prev, Arrived: newValue };
                            console.log('Updated registration state:', updated);
                            return updated;
                          });
                        } catch (error) {
                          console.error('Error updating Arrived status:', error);
                          alert('Failed to update Arrived status. Please try again.');
                        }
                      }}
                    />
                    <label className="form-check-label" htmlFor="arrived">
                      <span style={{ fontWeight: 'bold', color: '#333' }}>
                        Arrived
                        {registration?.Arrived && (
                          <span style={{ color: '#28a745', marginLeft: '8px' }}>
                            {new Date(registration.Arrived).toLocaleString()}
                          </span>
                        )}
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Second card - Primary Contact */}
              <RegistrationInfoCard
                registration={registration}
                parBalance={parBalance}
                parLoading={parLoading}
                onRegistrationUpdate={setRegistration}
              />

              {/* Third card - Stay Details */}
              <StayDetailsCard
                registration={registration}
                onEditClick={handleEditDatesClick}
              />

              {/* Fourth card - Accommodation Assignment */}
              <AccommodationAssignmentCard
                registration={registration}
                campSettings={campSettings}
                onRegistrationUpdate={setRegistration}
              />

              {/* Fifth card - Attending Campers */}
              <AttendingCampersCard
                registration={registration}
                campSettings={campSettings}
                onRegistrationUpdate={setRegistration}
              />

              {/* Stay History Card */}
              <StayHistoryCard registration={registration} />

              {/* Notes Card */}
              <NotesCard registration={registration} />
            </div>
            <div className="col-md-6">
              {/* Financial Summary Card */}
              <div className="card mb-3">
                <div className="card-header">
                  <h6 className="card-title mb-0" style={{ fontWeight: 'bold', color: '#333' }}>Financial Summary</h6>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th style={{ border: 'none', padding: '0.25rem 0' }}>Description</th>
                          <th style={{ border: 'none', padding: '0.25rem 0', textAlign: 'right' }}>Amount</th>
                          <th style={{ border: 'none', padding: '0.25rem 0', textAlign: 'right' }}>Subtotal</th>
                          <th style={{ border: 'none', padding: '0.25rem 0' }}>Notes</th>
                        </tr>
                        <tr>
                          <th style={{ border: 'none', padding: '0.25rem 0' }}><hr style={{ margin: '0.5rem 0' }} /></th>
                          <th style={{ border: 'none', padding: '0.25rem 0' }}><hr style={{ margin: '0.5rem 0' }} /></th>
                          <th style={{ border: 'none', padding: '0.25rem 0' }}><hr style={{ margin: '0.5rem 0' }} /></th>
                          <th style={{ border: 'none', padding: '0.25rem 0' }}><hr style={{ margin: '0.5rem 0' }} /></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const financialData: Array<{
                            description: string, 
                            amount: number, 
                            isSubtotal?: boolean, 
                            isTotal?: boolean, 
                            isSectionHeader?: boolean,
                            isMealFeesHeader?: boolean,
                            hasOverrideInput?: boolean,
                            hasOverrideCheckbox?: boolean,
                            hasFridgeCheckbox?: boolean,
                            hasDonationCheckbox?: boolean,
                            hasOtherFeesCheckbox?: boolean,
                            section?: string
                          }> = [];
                          let grandTotal = 0;

                          // Accommodation Fees
                          if (registration?.financial?.accommodationFees?.assignedAccommodation) {
                            const accommodation = registration.financial.accommodationFees;
                            
                            // Calculate nights from camp dates
                            let nights = 0;
                            if (registration?.campDates?.arrivalDate && registration?.campDates?.departureDate) {
                              const arrival = new Date(registration.campDates.arrivalDate);
                              const departure = new Date(registration.campDates.departureDate);
                              const timeDiff = departure.getTime() - arrival.getTime();
                              nights = Math.ceil(timeDiff / (1000 * 3600 * 24));
                            }
                            
                            const isFullCamp = nights >= 5;
                            const nightsText = isFullCamp ? 'Full Camp' : `${nights} nights`;
                            
                            // Calculate accommodation fee based on price code and nights
                            let accommodationFee = 0;
                            if (campSettings?.rates?.accommodation && accommodation.priceCode) {
                              const priceCode = String(accommodation.priceCode).toUpperCase();
                              const dormRates = campSettings.rates.accommodation.dorm;

                              // Dorm/Fireside fix: D1/D2 always come from the dorm rates bucket.
                              if (priceCode.startsWith('D') && dormRates?.[priceCode]) {
                                const rate = dormRates[priceCode];
                                if (rate.weekly && rate.nightly) {
                                  accommodationFee = isFullCamp ? rate.weekly : (rate.nightly * nights);
                                }
                              } else {
                                // Existing lookup logic for all non-dorm buckets.
                                const accommodationTypes = ['apartments', 'campsite', 'cottages', 'fireside', 'lowerCourt', 'upperCourt', 'dormA', 'dormB', 'privateCottage'];
                                
                                for (const type of accommodationTypes) {
                                  if (campSettings.rates.accommodation[type] && campSettings.rates.accommodation[type][priceCode]) {
                                    const rate = campSettings.rates.accommodation[type][priceCode];
                                    if (rate.weekly && rate.nightly) {
                                      accommodationFee = isFullCamp ? rate.weekly : (rate.nightly * nights);
                                      break;
                                    }
                                  }
                                }
                              }
                            }
                            
                            financialData.push({
                              description: 'Accommodation Fees',
                              amount: 0,
                              isSectionHeader: true
                            });
                            
                            financialData.push({
                              description: `${accommodation.placeName} (${nightsText})`,
                              amount: accommodationFee
                            });
                            
                            // Add override if checkbox is checked
                            if (showOverrides.accommodation) {
                              financialData.push({
                                description: 'Accommodation Override',
                                amount: overrides.accommodation.amount,
                                hasOverrideInput: true,
                                section: 'accommodation'
                              });
                            }
                            
                            const accommodationSubtotal = accommodationFee + (showOverrides.accommodation ? overrides.accommodation.amount : 0);
                            financialData.push({
                              description: 'Subtotal Accommodation',
                              amount: accommodationSubtotal,
                              isSubtotal: true,
                              hasOverrideCheckbox: true,
                              section: 'accommodation'
                            });
                            
                            grandTotal += accommodationSubtotal;
                          }

                          // Registration Fees
                          if (registration?.attendingCampers && registration.attendingCampers.length > 0) {
                            let registrationSubtotal = 0;
                            
                            financialData.push({
                              description: 'Registration Fees',
                              amount: 0,
                              isSectionHeader: true
                            });
                            
                            registration.attendingCampers.forEach((camper: any) => {
                              // Use individual camper nights if available, otherwise calculate from camp dates
                              let nights = camper.nights;
                              if (!nights && registration?.campDates?.arrivalDate && registration?.campDates?.departureDate) {
                                const arrival = new Date(registration.campDates.arrivalDate);
                                const departure = new Date(registration.campDates.departureDate);
                                const timeDiff = departure.getTime() - arrival.getTime();
                                nights = Math.ceil(timeDiff / (1000 * 3600 * 24));
                              }
                              nights = nights || 0;
                              
                              const isFullCamp = nights >= 5;
                              const nightsText = isFullCamp ? 'full Camp' : `${nights} nights`;
                              
                              let rate = 0;
                              if (campSettings?.rates?.registration) {
                                if (camper.age === 'Adult') {
                                  rate = isFullCamp ? campSettings.rates.registration.adult.weekly : campSettings.rates.registration.adult.nightly;
                                } else if (camper.age === 'AdultStudent') {
                                  rate = isFullCamp ? campSettings.rates.registration.adultStudent.weekly : campSettings.rates.registration.adultStudent.nightly;
                                } else if (camper.age === 'YouthChildren') {
                                  rate = isFullCamp ? campSettings.rates.registration.youthAndChildren.weekly : campSettings.rates.registration.youthAndChildren.nightly;
                                }
                              }
                              
                              const camperFee = isFullCamp ? rate : (rate * nights);
                              registrationSubtotal += camperFee;
                              
                              financialData.push({
                                description: `${camper.firstName} ${camper.lastName} - ${camper.age} (${nightsText})`,
                                amount: camperFee
                              });
                            });
                            
                            // Add override if checkbox is checked
                            if (showOverrides.registration) {
                              financialData.push({
                                description: 'Registration Override',
                                amount: overrides.registration.amount,
                                hasOverrideInput: true,
                                section: 'registration'
                              });
                            }
                            
                            const registrationSubtotalWithOverride = registrationSubtotal + (showOverrides.registration ? overrides.registration.amount : 0);
                            financialData.push({
                              description: 'Subtotal Registration Fees',
                              amount: registrationSubtotalWithOverride,
                              isSubtotal: true,
                              hasOverrideCheckbox: true,
                              section: 'registration'
                            });
                            
                            grandTotal += registrationSubtotalWithOverride;
                          }

                          // Meal Fees - check both new path (financial.otherFees.meals) and legacy paths
                          const meals = registration?.financial?.otherFees?.meals;
                          const legacyMeals = (registration as any)?.financial?.mealFees || (registration as any)?.mealFees;
                          console.log('Meal Fees Data:', { meals, legacyMeals });
                          
                          financialData.push({
                            description: 'Meal Fees',
                            amount: 0,
                            isSectionHeader: true,
                            isMealFeesHeader: true
                          });
                          
                          // Get meal rates from settings
                          const fullCampAdultRate = campSettings?.rates?.meals?.fullCampAdult || 100;
                          const fullCampChildRate = campSettings?.rates?.meals?.fullCampChild || 50;
                          const dailyAdultRate = campSettings?.rates?.meals?.dailyAdult || 15;
                          const dailyChildRate = campSettings?.rates?.meals?.dailyChild || 10;
                          
                          // Extract quantities - try new structure first, then legacy
                          let fullCampAdult = 0, fullCampChild = 0, dailyAdult = 0, dailyChild = 0;
                          
                          if (meals) {
                            fullCampAdult = meals.fullCampPassesAdult?.quantity || 0;
                            fullCampChild = meals.fullCampPassesChild?.quantity || 0;
                            dailyAdult = meals.dailyPassesAdult?.quantity || 0;
                            dailyChild = meals.dailyPassesChild?.quantity || 0;
                          } else if (legacyMeals) {
                            fullCampAdult = legacyMeals.fullCampPassesAdult || legacyMeals.fullCampPasses?.adult || 0;
                            fullCampChild = legacyMeals.fullCampPassesChild || legacyMeals.fullCampPasses?.child || 0;
                            dailyAdult = legacyMeals.dailyPassesAdult || legacyMeals.dailyPasses?.adult || 0;
                            dailyChild = legacyMeals.dailyPassesChild || legacyMeals.dailyPasses?.child || 0;
                          }
                          
                          let mealSubtotal = 0;
                          
                          if (fullCampAdult > 0) {
                            const amount = fullCampAdult * fullCampAdultRate;
                            mealSubtotal += amount;
                            financialData.push({
                              description: `Full Camp Pass (Adult) x${fullCampAdult}`,
                              amount: amount
                            });
                          }
                          
                          if (fullCampChild > 0) {
                            const amount = fullCampChild * fullCampChildRate;
                            mealSubtotal += amount;
                            financialData.push({
                              description: `Full Camp Pass (Child) x${fullCampChild}`,
                              amount: amount
                            });
                          }
                          
                          if (dailyAdult > 0) {
                            const amount = dailyAdult * dailyAdultRate;
                            mealSubtotal += amount;
                            financialData.push({
                              description: `Daily Pass (Adult) x${dailyAdult}`,
                              amount: amount
                            });
                          }
                          
                          if (dailyChild > 0) {
                            const amount = dailyChild * dailyChildRate;
                            mealSubtotal += amount;
                            financialData.push({
                              description: `Daily Pass (Child) x${dailyChild}`,
                              amount: amount
                            });
                          }
                          
                          // Show message if no meal passes ordered
                          if (mealSubtotal === 0) {
                            financialData.push({
                              description: 'No meal passes ordered',
                              amount: 0
                            });
                          }
                          
                          // Add override if checkbox is checked
                          if (showOverrides.mealFees) {
                            financialData.push({
                              description: 'Meal Fees Override',
                              amount: overrides.mealFees.amount,
                              hasOverrideInput: true,
                              section: 'mealFees'
                            });
                          }
                          
                          const mealSubtotalWithOverride = mealSubtotal + (showOverrides.mealFees ? overrides.mealFees.amount : 0);
                          if (mealSubtotalWithOverride > 0 || showOverrides.mealFees) {
                            financialData.push({
                              description: 'Subtotal meal fees',
                              amount: mealSubtotalWithOverride,
                              isSubtotal: true,
                              hasOverrideCheckbox: true,
                              section: 'mealFees'
                            });
                            grandTotal += mealSubtotalWithOverride;
                          }

                          // Other Fees
                          const otherFees = registration?.financial?.otherFees;
                          console.log('Other Fees Data:', otherFees);
                          
                          financialData.push({
                            description: 'Other Fees',
                            amount: 0,
                            isSectionHeader: true
                          });
                          
                          let otherSubtotal = 0;
                          const fridgeRate = campSettings?.rates?.otherFees?.fridge || 25;
                          
                          // Check for fridge rental
                          const hasFridge = otherFees?.fridge?.requested === true || (otherFees?.fridge?.cost || 0) > 0 || (otherFees?.fridgeRental || 0) > 0;
                          const fridgeRental = hasFridge ? (otherFees?.fridge?.cost || otherFees?.fridgeRental || fridgeRate) : 0;
                          
                          // Check for donation
                          const donationValue = otherFees?.donations?.amount || otherFees?.donation || 0;
                          
                          // Fridge row with checkbox
                          financialData.push({
                            description: 'Fridge',
                            amount: fridgeRental,
                            hasFridgeCheckbox: true
                          });
                          if (hasFridge) {
                            otherSubtotal += fridgeRental;
                          }
                          
                          // Donation row with checkbox
                          const donationNoteValue = (registration?.financial?.otherFees?.donations as any)?.note || '';
                          financialData.push({
                            description: donationNoteValue ? `Donation (${donationNoteValue})` : 'Donation',
                            amount: donationValue,
                            hasDonationCheckbox: true
                          });
                          if (donationValue > 0) {
                            otherSubtotal += donationValue;
                          }
                          
                          // Other Fees row with checkbox (admin-only)
                          const customFeeValue = (otherFees as any)?.customFee?.amount || 0;
                          const customFeeNote = (otherFees as any)?.customFee?.note || '';
                          financialData.push({
                            description: customFeeNote ? `Other Fees (${customFeeNote})` : 'Other Fees',
                            amount: customFeeValue,
                            hasOtherFeesCheckbox: true
                          });
                          if (customFeeValue > 0) {
                            otherSubtotal += customFeeValue;
                          }

                          const otherFeesArray = (otherFees as any)?.otherFees || [];
                          for (const fee of otherFeesArray) {
                            const feeAmount = Number(fee.amount) || 0;
                            if (feeAmount > 0) {
                              financialData.push({
                                description: fee.note || 'Fee',
                                amount: feeAmount,
                              });
                              otherSubtotal += feeAmount;
                            }
                          }
                          
                          // Add override if checkbox is checked
                          if (showOverrides.otherFees) {
                            financialData.push({
                              description: 'Other Fees Override',
                              amount: overrides.otherFees.amount,
                              hasOverrideInput: true,
                              section: 'otherFees'
                            });
                          }
                          
                          const otherSubtotalWithOverride = otherSubtotal + (showOverrides.otherFees ? overrides.otherFees.amount : 0);
                          financialData.push({
                            description: 'Total Other fees',
                            amount: otherSubtotalWithOverride,
                            isSubtotal: true,
                            hasOverrideCheckbox: true,
                            section: 'otherFees'
                          });
                          grandTotal += otherSubtotalWithOverride;

                    // Add total fees
                    if (grandTotal > 0) {
                      financialData.push({
                        description: 'Total Fees',
                        amount: grandTotal,
                        isTotal: true
                      });
                    }
                    
                    // Add total balance (Total Fees - Payments)
                    if (grandTotal > 0) {
                      const totalPayments = payments.reduce((sum, payment) => sum + payment.amount, 0);
                      const calculatedTotalBalance = grandTotal - totalPayments;
                      financialData.push({
                        description: 'Total Balance',
                        amount: calculatedTotalBalance,
                        isTotal: true
                      });
                    }

                          return financialData.map((item, index) => (
                            <React.Fragment key={index}>
                              <tr>
                                <td style={{ 
                                  border: 'none', 
                                  padding: '0.25rem 0',
                                  fontWeight: item.isSectionHeader || item.isSubtotal || item.isTotal ? 'bold' : 'normal',
                                  borderTop: item.isTotal ? '2px solid #333' : 'none',
                                  paddingTop: item.isTotal ? '0.5rem' : '0.25rem'
                                }}>
                                  {item.description}
                                  {item.isMealFeesHeader && (
                                    <button
                                      onClick={() => {
                                        // Get current values from registration
                                        const meals = registration?.financial?.otherFees?.meals;
                                        const legacyMeals = (registration as any)?.financial?.mealFees || (registration as any)?.mealFees;
                                        
                                        let fca = 0, fcc = 0, da = 0, dc = 0;
                                        if (meals) {
                                          fca = meals.fullCampPassesAdult?.quantity || 0;
                                          fcc = meals.fullCampPassesChild?.quantity || 0;
                                          da = meals.dailyPassesAdult?.quantity || 0;
                                          dc = meals.dailyPassesChild?.quantity || 0;
                                        } else if (legacyMeals) {
                                          fca = legacyMeals.fullCampPassesAdult || legacyMeals.fullCampPasses?.adult || 0;
                                          fcc = legacyMeals.fullCampPassesChild || legacyMeals.fullCampPasses?.child || 0;
                                          da = legacyMeals.dailyPassesAdult || legacyMeals.dailyPasses?.adult || 0;
                                          dc = legacyMeals.dailyPassesChild || legacyMeals.dailyPasses?.child || 0;
                                        }
                                        
                                        setEditMealFees({
                                          fullCampAdult: fca,
                                          fullCampChild: fcc,
                                          dailyAdult: da,
                                          dailyChild: dc
                                        });
                                        setShowMealFeesModal(true);
                                      }}
                                      style={{
                                        marginLeft: '8px',
                                        padding: '2px 8px',
                                        fontSize: '0.75rem',
                                        backgroundColor: '#007bff',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      Edit
                                    </button>
                                  )}
                                  {item.hasOverrideCheckbox && item.section && (
                                    <span className="ms-2">
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={showOverrides[item.section as keyof typeof showOverrides]}
                                        onChange={() => handleOverrideToggle(item.section!)}
                                        style={{ 
                                          transform: 'scale(0.8)', 
                                          borderColor: '#007bff',
                                          backgroundColor: showOverrides[item.section as keyof typeof showOverrides] ? '#007bff' : 'white',
                                          boxShadow: '0 2px 4px rgba(0, 123, 255, 0.3)'
                                        }}
                                      />
                                      <span className="ms-1" style={{ fontSize: '0.8rem' }}>override</span>
                                    </span>
                                  )}
                                </td>
                                <td style={{ 
                                  border: 'none', 
                                  padding: '0.25rem 0',
                                  textAlign: 'right',
                                  fontWeight: 'normal',
                                  borderTop: item.isTotal ? '2px solid #333' : 'none',
                                  paddingTop: item.isTotal ? '0.5rem' : '0.25rem'
                                }}>
                                  {item.hasOverrideInput && item.section ? (
                                    <input
                                      type="number"
                                      className="form-control form-control-sm"
                                      style={{ width: '80px', display: 'inline-block' }}
                                      value={overrides[item.section as keyof typeof overrides].amount || ''}
                                      onChange={(e) => handleOverrideChange(item.section!, 'amount', parseFloat(e.target.value) || 0)}
                                      step="0.01"
                                      placeholder="0.00"
                                    />
                                  ) : item.hasFridgeCheckbox ? (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={item.amount > 0}
                                        onChange={(e) => handleFridgeToggle(e.target.checked)}
                                        style={{ margin: 0 }}
                                      />
                                      <span>{item.amount > 0 ? `$${item.amount.toFixed(2)}` : '$0.00'}</span>
                                    </div>
                                  ) : item.hasDonationCheckbox ? (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={item.amount > 0 || showDonationInput}
                                        onChange={(e) => handleDonationToggle(e.target.checked)}
                                        style={{ margin: 0 }}
                                      />
                                      {showDonationInput ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
                                          <div style={{ display: 'flex', gap: '4px' }}>
                                            <input
                                              type="number"
                                              className="form-control form-control-sm"
                                              style={{ width: '80px' }}
                                              value={donationAmount || ''}
                                              onChange={(e) => setDonationAmount(parseFloat(e.target.value) || 0)}
                                              placeholder="0.00"
                                              step="0.01"
                                            />
                                            <button
                                              className="btn btn-sm btn-primary"
                                              onClick={handleDonationSave}
                                              disabled={donationAmount <= 0}
                                            >
                                              Save
                                            </button>
                                          </div>
                                          <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            style={{ width: '200px' }}
                                            value={donationNote}
                                            onChange={(e) => setDonationNote(e.target.value.slice(0, 256))}
                                            placeholder="Note (optional)"
                                            maxLength={256}
                                          />
                                        </div>
                                      ) : (
                                        <span>{item.amount > 0 ? `$${item.amount.toFixed(2)}` : '$0.00'}</span>
                                      )}
                                    </div>
                                  ) : item.hasOtherFeesCheckbox ? (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={item.amount > 0 || showOtherFeesInput}
                                        onChange={(e) => handleOtherFeesToggle(e.target.checked)}
                                        style={{ margin: 0 }}
                                      />
                                      {showOtherFeesInput ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
                                          <div style={{ display: 'flex', gap: '4px' }}>
                                            <input
                                              type="number"
                                              className="form-control form-control-sm"
                                              style={{ width: '80px' }}
                                              value={otherFeesAmount || ''}
                                              onChange={(e) => setOtherFeesAmount(parseFloat(e.target.value) || 0)}
                                              placeholder="0.00"
                                              step="0.01"
                                            />
                                            <button
                                              className="btn btn-sm btn-primary"
                                              onClick={handleOtherFeesSave}
                                              disabled={otherFeesAmount <= 0}
                                            >
                                              Save
                                            </button>
                                          </div>
                                          <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            style={{ width: '200px' }}
                                            value={otherFeesNote}
                                            onChange={(e) => setOtherFeesNote(e.target.value.slice(0, 256))}
                                            placeholder="Note (optional)"
                                            maxLength={256}
                                          />
                                        </div>
                                      ) : (
                                        <span>{item.amount > 0 ? `$${item.amount.toFixed(2)}` : '$0.00'}</span>
                                      )}
                                    </div>
                                  ) : (
                                    item.isSectionHeader || item.isSubtotal || item.isTotal ? '' : `$${item.amount.toFixed(2)}`
                                  )}
                                </td>
                                <td style={{ 
                                  border: 'none', 
                                  padding: '0.25rem 0',
                                  textAlign: 'right',
                                  fontWeight: item.isSubtotal || item.isTotal ? 'bold' : 'normal',
                                  borderTop: item.isTotal ? '2px solid #333' : 'none',
                                  paddingTop: item.isTotal ? '0.5rem' : '0.25rem'
                                }}>
                                  {item.isSubtotal || item.isTotal ? `$${item.amount.toFixed(2)}` : ''}
                                </td>
                                <td style={{ 
                                  border: 'none', 
                                  padding: '0.25rem 0',
                                  borderTop: item.isTotal ? '2px solid #333' : 'none',
                                  paddingTop: item.isTotal ? '0.5rem' : '0.25rem'
                                }}>
                                  {/* Notes column - empty for now */}
                                </td>
                              </tr>
                              {/* Add notes row for override inputs */}
                              {item.hasOverrideInput && item.section && (
                                <tr>
                                  <td colSpan={4} style={{ 
                                    border: 'none', 
                                    padding: '0.25rem 0 0.5rem 0'
                                  }}>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      style={{ width: '100%', maxWidth: '400px' }}
                                      placeholder="Notes (e.g., Family discount applied)"
                                      value={overrides[item.section as keyof typeof overrides].notes}
                                      onChange={(e) => handleOverrideChange(item.section!, 'notes', e.target.value)}
                                    />
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              
              {/* Payment History Card */}
              <div className="card mb-3">
                <div className="card-header">
                  <h6 className="card-title mb-0" style={{ fontWeight: 'bold', color: '#333' }}>Payment History</h6>
                </div>
                <div className="card-body">
                  {/* Payment List */}
                  {payments.length > 0 ? (
                    <div className="table-responsive mb-3">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Method</th>
                            <th>Amount</th>
                            <th>Reference Note</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payments.map((payment: any) => {
                            const isRefund = payment.method === 'Refund' || payment.amount < 0;
                            const isCreditCard = payment.method === 'CreditCard' && !!payment.stripePaymentId;
                            const canRefund = isCreditCard && !payment.refunded && !isRefund && payment.amount > 0;
                            const isFromInbox = !!payment.etransferInboxId;
                            
                            return (
                              <tr 
                                key={payment.id}
                                onClick={() => {
                                  if (canRefund) {
                                    setSelectedPaymentForRefund(payment);
                                    setShowRefundModal(true);
                                  }
                                }}
                                style={{ 
                                  backgroundColor: isRefund ? '#fff3cd' : payment.refunded ? '#f8f9fa' : 'inherit',
                                  opacity: payment.refunded ? 0.7 : 1,
                                  cursor: canRefund ? 'pointer' : 'default'
                                }}
                                title={canRefund ? 'Click to refund this payment' : ''}
                              >
                                <td>{formatPaymentDate(payment.date)}</td>
                                <td>
                                  {payment.method}
                                  {payment.refunded && (
                                    <span className="badge bg-secondary ms-2" style={{ fontSize: '0.7em' }}>
                                      Refunded
                                    </span>
                                  )}
                                </td>
                                <td style={{ color: isRefund ? '#dc3545' : 'inherit', fontWeight: isRefund ? 'bold' : 'normal' }}>
                                  {isRefund ? (
                                    <>-${Math.abs(payment.amount).toFixed(2)}</>
                                  ) : (
                                    <>
                                      ${Math.abs(payment.amount).toFixed(2)}
                                      {payment.convenienceFee ? (
                                        <span style={{ fontSize: '0.85em', color: '#6c757d', marginLeft: '4px' }}>
                                          (incl ${Math.abs(payment.convenienceFee).toFixed(2)} fee)
                                        </span>
                                      ) : null}
                                    </>
                                  )}
                                </td>
                                <td title={payment.referenceNote || ''}>
                                  {payment.referenceNote 
                                    ? (payment.referenceNote.length > 25 
                                        ? `${payment.referenceNote.substring(0, 25)}...` 
                                        : payment.referenceNote)
                                    : '-'}
                                </td>
                                <td>
                                  {canRefund ? (
                                    <Button 
                                      variant="outline-warning" 
                                      size="sm" 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedPaymentForRefund(payment);
                                        setShowRefundModal(true);
                                      }}
                                    >
                                      Refund
                                    </Button>
                                  ) : isFromInbox && !isRefund ? (
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      disabled={undoingPaymentId === payment.id}
                                      title="Remove this payment and return the e-transfer to the inbox as un-applied"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleUndoEtransfer(payment);
                                      }}
                                    >
                                      {undoingPaymentId === payment.id ? 'Undoing...' : 'Undo'}
                                    </Button>
                                  ) : !isCreditCard && !isRefund ? (
                                    <Button 
                                      variant="outline-danger" 
                                      size="sm" 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeletePayment(payment.id);
                                      }}
                                    >
                                      Delete
                                    </Button>
                                  ) : null}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-muted mb-3">No payments recorded yet.</p>
                  )}
                  
                  
                  {/* Add Payment Section */}
                  <div className="border-top pt-3">
                    <h6 className="mb-3" style={{ fontWeight: 'bold', color: '#333' }}>Add Payment</h6>
                    
                    <div className="row">
                      <div className="col-md-4">
                        <label className="form-label">Payment Method</label>
                        <select
                          className="form-select form-select-sm"
                          value={newPayment.method}
                          onChange={(e) => setNewPayment({...newPayment, method: e.target.value})}
                        >
                          <option value="">Select method</option>
                          <option value="Cash/Check">Cash/Check</option>
                          <option value="Etransfer">Etransfer</option>
                          <option value="Par">Par</option>
                          <option value="Credit Card">Credit Card</option>
                          <option value="Moneris PinPad">Moneris PinPad</option>
                          <option value="Program Committee">Program Committee</option>
                          <option value="Benevolent Fund">Benevolent Fund</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      
                      <div className="col-md-3">
                        <label className="form-label">Amount</label>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          placeholder="0.00"
                          value={newPayment.amount}
                          onChange={(e) => setNewPayment({...newPayment, amount: e.target.value})}
                          step="0.01"
                          min="0"
                        />
                      </div>
                      
                      <div className="col-md-5 d-flex align-items-end flex-wrap gap-1">
                        <Button 
                          variant="primary" 
                          size="sm" 
                          onClick={handleAddPayment}
                          disabled={!newPayment.method || !newPayment.amount || (newPayment.method === 'Other' && !newPayment.referenceNote.trim())}
                        >
                          Add Payment
                        </Button>
                        <Button 
                          variant="success" 
                          size="sm" 
                          onClick={handleApplyParPayment}
                        >
                          Apply Par
                        </Button>
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={handleResendInvoice}
                          disabled={sendingInvoice}
                        >
                          {sendingInvoice ? 'Sending...' : 'Send Invoice'}
                        </Button>
                      </div>
                    </div>
                    
                    <div className="row mt-2">
                      <div className="col-md-10">
                        <label className="form-label">
                          {newPayment.method === 'Other' ? "Reference Note (required — explain 'Other')" : 'Reference Note'}
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Enter reference note (e.g., check #1234, transaction ID)"
                          value={newPayment.referenceNote}
                          onChange={(e) => setNewPayment({...newPayment, referenceNote: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Hold and Release Reservation Cards */}
              <div className="row">
                <div className="col-md-6">
                  <HoldReservationCard
                    ref={holdReservationRef}
                    registration={registration}
                    onRegistrationUpdate={setRegistration}
                  />
                </div>
                <div className="col-md-6">
                  <ReleaseReservationCard
                    ref={releaseReservationRef}
                    registration={registration}
                    onRegistrationUpdate={setRegistration}
                    onSendInvoiceEmail={sendInvoiceEmail}
                  />
                </div>
              </div>

              {/* Undo Reservation Card */}
              <UndoReservationCard
                registration={registration}
                onRegistrationUpdate={setRegistration}
                onResetHoldSteps={() => holdReservationRef.current?.resetSteps()}
                onResetReleaseStep={() => releaseReservationRef.current?.resetStep()}
              />
            </div>
          </div>
        </div>
      </div>

      {invoiceDisplayRegistration && (
        <div
          aria-hidden="true"
          style={{
            position: 'fixed',
            left: '-10000px',
            top: 0,
            width: '816px',
            pointerEvents: 'none',
          }}
        >
          <InvoiceDisplay
            containerId="registration-invoice-source"
            registration={invoiceDisplayRegistration}
            invoiceNumber={registration?.invoiceNumber || 'Not Finalized'}
            invoiceDate={invoiceDateForDisplay}
          />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={handleDeleteCancel}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete the registration for:</p>
                <p><strong>{registration.registrationInfo?.primaryCamperName}</strong></p>
                <p>Email: {registration.registrationInfo?.email}</p>
                <p className="text-danger">This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <Button variant="secondary" onClick={handleDeleteCancel}>
                  Cancel
                </Button>
                <Button variant="danger" onClick={handleDeleteConfirm} disabled={isDeleting}>
                  {isDeleting ? 'Deleting...' : 'Delete Registration'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Dates Modal */}
      {showEditDates && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Stay Dates</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={handleEditDatesCancel}
                ></button>
              </div>
              <div className="modal-body">
                {campSettings && (
                  <div className="mb-3">
                    <small className="text-muted">
                      Camp season: {new Date(campSettings.startDate).toLocaleDateString()} to {new Date(campSettings.endDate).toLocaleDateString()}
                    </small>
                    <br />
                    <small className="text-muted">
                      Debug - Min: {campSettings?.startDate ? (typeof campSettings.startDate === 'string' ? campSettings.startDate : new Date(campSettings.startDate).toISOString().split('T')[0]) : 'undefined'}, 
                      Max: {campSettings?.endDate ? (typeof campSettings.endDate === 'string' ? campSettings.endDate : new Date(campSettings.endDate).toISOString().split('T')[0]) : 'undefined'}
                    </small>
                  </div>
                )}
                
                <div className="mb-3">
                  <label className="form-label">Arrival Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={editArrivalDate}
                    onChange={(e) => {
                      console.log('Arrival date changed to:', e.target.value);
                      setEditArrivalDate(e.target.value);
                    }}
                    onFocus={() => console.log('Arrival date input focused')}
                    onClick={() => console.log('Arrival date input clicked')}
                    min={campStartMinDate}
                    max={campEndMaxDate}
                  />
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Departure Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={editDepartureDate}
                    onChange={(e) => {
                      console.log('Departure date changed to:', e.target.value);
                      setEditDepartureDate(e.target.value);
                    }}
                    onFocus={() => console.log('Departure date input focused')}
                    onClick={() => console.log('Departure date input clicked')}
                    min={editArrivalDate || campStartMinDate}
                    max={campEndMaxDate}
                  />
                </div>
                
                {editArrivalDate && editDepartureDate && (
                  <div className="alert alert-info">
                    <strong>Stay Duration:</strong> {calculateNights(editArrivalDate, editDepartureDate)} nights
                    {calculateNights(editArrivalDate, editDepartureDate) >= 5 && ' (Full Camp)'}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <Button variant="secondary" onClick={handleEditDatesCancel}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleEditDatesSave} disabled={!editArrivalDate || !editDepartureDate}>
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      <Modal show={showInvoice} onHide={() => setShowInvoice(false)} size="lg" className="invoice-modal">
        <Modal.Header closeButton>
          <Modal.Title>Invoice - {registration?.firstName} {registration?.lastName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {invoiceDisplayRegistration && (
              <InvoiceDisplay
              registration={invoiceDisplayRegistration}
              invoiceNumber={registration?.invoiceNumber || 'Not Finalized'}
              invoiceDate={invoiceDateForDisplay}
              />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-info"
            onClick={handleOpenCustomEmailModal}
            disabled={sendingInvoice}
          >
            Email Invoice Again
          </Button>
          <Button variant="secondary" onClick={() => setShowInvoice(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Email Invoice Again - custom message modal */}
      <Modal show={showCustomEmailModal} onHide={() => setShowCustomEmailModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Email Invoice</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Subject</label>
            <input
              type="text"
              className="form-control"
              value="Berwick Camp Information"
              readOnly
              disabled
            />
          </div>
          <div>
            <label className="form-label">Message</label>
            <textarea
              className="form-control"
              rows={8}
              placeholder="Type the message to include in the body of the email..."
              value={customEmailBody}
              onChange={(e) => setCustomEmailBody(e.target.value)}
              autoFocus
            />
            <small className="text-muted">
              The email will greet the camper and include your message above. The invoice PDF is attached automatically.
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCustomEmailModal(false)} disabled={sendingInvoice}>
            Cancel
          </Button>
          <Button
            variant="info"
            onClick={handleSendCustomInvoiceEmail}
            disabled={sendingInvoice || !customEmailBody.trim()}
          >
            {sendingInvoice ? 'Sending...' : 'Send Email'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Meal Fees Edit Modal */}
      <Modal show={showMealFeesModal} onHide={() => setShowMealFeesModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Meal Fees</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '12px', textAlign: 'left' }}>Pass Type</th>
                <th style={{ padding: '12px', textAlign: 'center', width: '80px' }}>Qty</th>
                <th style={{ padding: '12px', textAlign: 'right' }}>Unit Price</th>
                <th style={{ padding: '12px', textAlign: 'right' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                <td style={{ padding: '12px' }}>Full Camp Pass - Adult</td>
                <td style={{ padding: '12px', textAlign: 'center' }}>
                  <input
                    type="number"
                    min="0"
                    value={editMealFees.fullCampAdult}
                    onChange={(e) => setEditMealFees(prev => ({ ...prev, fullCampAdult: parseInt(e.target.value) || 0 }))}
                    style={{ width: '70px', textAlign: 'center', padding: '6px', border: '1px solid #ced4da', borderRadius: '4px' }}
                  />
                </td>
                <td style={{ padding: '12px', textAlign: 'right' }}>${(campSettings?.rates?.meals?.fullCampAdult || 100).toFixed(2)}</td>
                <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold' }}>${(editMealFees.fullCampAdult * (campSettings?.rates?.meals?.fullCampAdult || 100)).toFixed(2)}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                <td style={{ padding: '12px' }}>Full Camp Pass - Child</td>
                <td style={{ padding: '12px', textAlign: 'center' }}>
                  <input
                    type="number"
                    min="0"
                    value={editMealFees.fullCampChild}
                    onChange={(e) => setEditMealFees(prev => ({ ...prev, fullCampChild: parseInt(e.target.value) || 0 }))}
                    style={{ width: '70px', textAlign: 'center', padding: '6px', border: '1px solid #ced4da', borderRadius: '4px' }}
                  />
                </td>
                <td style={{ padding: '12px', textAlign: 'right' }}>${(campSettings?.rates?.meals?.fullCampChild || 50).toFixed(2)}</td>
                <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold' }}>${(editMealFees.fullCampChild * (campSettings?.rates?.meals?.fullCampChild || 50)).toFixed(2)}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                <td style={{ padding: '12px' }}>Daily Pass - Adult</td>
                <td style={{ padding: '12px', textAlign: 'center' }}>
                  <input
                    type="number"
                    min="0"
                    value={editMealFees.dailyAdult}
                    onChange={(e) => setEditMealFees(prev => ({ ...prev, dailyAdult: parseInt(e.target.value) || 0 }))}
                    style={{ width: '70px', textAlign: 'center', padding: '6px', border: '1px solid #ced4da', borderRadius: '4px' }}
                  />
                </td>
                <td style={{ padding: '12px', textAlign: 'right' }}>${(campSettings?.rates?.meals?.dailyAdult || 15).toFixed(2)}</td>
                <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold' }}>${(editMealFees.dailyAdult * (campSettings?.rates?.meals?.dailyAdult || 15)).toFixed(2)}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                <td style={{ padding: '12px' }}>Daily Pass - Child</td>
                <td style={{ padding: '12px', textAlign: 'center' }}>
                  <input
                    type="number"
                    min="0"
                    value={editMealFees.dailyChild}
                    onChange={(e) => setEditMealFees(prev => ({ ...prev, dailyChild: parseInt(e.target.value) || 0 }))}
                    style={{ width: '70px', textAlign: 'center', padding: '6px', border: '1px solid #ced4da', borderRadius: '4px' }}
                  />
                </td>
                <td style={{ padding: '12px', textAlign: 'right' }}>${(campSettings?.rates?.meals?.dailyChild || 10).toFixed(2)}</td>
                <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold' }}>${(editMealFees.dailyChild * (campSettings?.rates?.meals?.dailyChild || 10)).toFixed(2)}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr style={{ backgroundColor: '#f8f9fa', borderTop: '2px solid #dee2e6' }}>
                <td colSpan={3} style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', fontSize: '1.1em' }}>Grand Total:</td>
                <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', fontSize: '1.1em', color: '#007bff' }}>
                  ${(
                    (editMealFees.fullCampAdult * (campSettings?.rates?.meals?.fullCampAdult || 100)) +
                    (editMealFees.fullCampChild * (campSettings?.rates?.meals?.fullCampChild || 50)) +
                    (editMealFees.dailyAdult * (campSettings?.rates?.meals?.dailyAdult || 15)) +
                    (editMealFees.dailyChild * (campSettings?.rates?.meals?.dailyChild || 10))
                  ).toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMealFeesModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveMealFees}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Refund Modal */}
      <RefundModal
        show={showRefundModal}
        onHide={() => {
          setShowRefundModal(false);
          setSelectedPaymentForRefund(null);
        }}
        payment={selectedPaymentForRefund}
        registrationId={registration?.id || ''}
        onRefundComplete={async () => {
          // Refresh registration data after refund
          if (id) {
            const registrationRef = doc(db, 'Registrations', id);
            const registrationSnap = await getDoc(registrationRef);
            if (registrationSnap.exists()) {
              const regData = registrationSnap.data();
              setRegistration({ id: registrationSnap.id, ...regData } as any);
              if ((regData as any).paymentHistory && Array.isArray((regData as any).paymentHistory)) {
                setPayments((regData as any).paymentHistory);
              }
            }
          }
        }}
      />
    </div>
  );
};

export default RegistrationDetailsPage;
